import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";
import { AppCSS } from "..";
import { CloseButton, TapButton } from "../buttons";
import { Lbl } from "../labels";
import { Spacer } from "../misc";
import { RowDiv } from "../misc/misc.styled";

interface DialogProps {
  open: boolean;
  onClose: Function;
  onConfirm: Function;
  message: string;
}

export const Prompt: React.FC<DialogProps> = ({
  open,
  onClose,
  message,
  onConfirm,
}) => {
  return (
    <Dialog
      open={open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
          minWidth: "340px",
          maxWidth: "90vw",
        }
      }}
    >
      <DialogTitle 
        id="alert-dialog-title"
        sx={{
          fontSize: "18px",
          fontWeight: 600,
          color: "#1f2937",
          pb: 1,
        }}
      >
        Please Confirm
      </DialogTitle>
      <DialogContent>
        <DialogContentText 
          id="alert-dialog-description"
          sx={{
            fontSize: "14px",
            color: "#6b7280",
            lineHeight: 1.6,
          }}
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px", gap: 1 }}>
        <Button 
          onClick={() => onClose()}
          sx={{
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 500,
            color: "#6b7280",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "8px 20px",
            "&:hover": {
              backgroundColor: "#f3f4f6",
              borderColor: "#d1d5db",
            }
          }}
        >
          No
        </Button>
        <Button 
          onClick={() => onConfirm()} 
          autoFocus
          sx={{
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 500,
            color: "white",
            backgroundColor: "#4b87ed",
            borderRadius: "8px",
            padding: "8px 20px",
            "&:hover": {
              backgroundColor: "#3b77dd",
            }
          }}
        >
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface AlertProps {
  open: boolean;
  onConfirm: Function;
  onClose?: Function;
  title: string;
  message: string;
  okayTitle?: string;
}

export const AppAlert: React.FC<AlertProps> = ({
  open,
  title,
  onConfirm,
  message,
  onClose,
  okayTitle,
}) => {
  return (
    <Dialog
      open={open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
          minWidth: "340px",
          maxWidth: "90vw",
        }
      }}
    >
      <DialogTitle 
        id="alert-dialog-title"
        sx={{
          fontSize: "18px",
          fontWeight: 600,
          color: "#1f2937",
          pb: 1,
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText 
          id="alert-dialog-description"
          sx={{
            fontSize: "14px",
            color: "#6b7280",
            lineHeight: 1.6,
          }}
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px", gap: 1 }}>
        {onClose && (
          <Button
            onClick={() => onClose()}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              color: "#6b7280",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              padding: "8px 20px",
              "&:hover": {
                backgroundColor: "#f3f4f6",
                borderColor: "#d1d5db",
              }
            }}
          >
            Cancel
          </Button>
        )}
        <Button
          onClick={() => onConfirm()}
          sx={{
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 500,
            color: "white",
            backgroundColor: title?.toLowerCase().includes("delete") ? "#ef4444" : "#4b87ed",
            borderRadius: "8px",
            padding: "8px 20px",
            "&:hover": {
              backgroundColor: title?.toLowerCase().includes("delete") ? "#dc2626" : "#3b77dd",
            }
          }}
        >
          {okayTitle ? okayTitle : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface CustomAlertProps {
  open: boolean;
  onConfirm?: unknown;
  onClose?: any;
  onCancel?: any;
  title?: string;
  Content?: ReactNode;
  width?: number;
  height?: number;
}

export const CustomAlert: React.FC<CustomAlertProps> = ({
  title,
  open,
  onConfirm,
  onClose,
  Content,
  onCancel,
  width,
}) => {
  return (
    <Dialog
      open={open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          minWidth: "340px",
          maxWidth: "90vw",
        }
      }}
    >
      {onCancel && (
        <RowDiv
          style={{
            justifyContent: "flex-end",
            width: "96%",
            marginTop: "10px",
          }}
        >
          <CloseButton onTap={() => (onCancel ? onCancel() : {})} />
        </RowDiv>
      )}
      {title && (
        <DialogTitle
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#1f2937",
            textAlign: "center",
            pb: 1,
          }}
        >
          {title}
        </DialogTitle>
      )}

      <DialogContent
        sx={{
          justifyContent: "center",
          padding: "20px",
          // width: width ? `${width}px` : `380px`,
        }}
      >
        {Content && Content}
      </DialogContent>
      {onConfirm && (
        <DialogActions>
          <RowDiv
            style={{
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            {onClose && onClose}
            {onConfirm}
          </RowDiv>
        </DialogActions>
      )}
      <Spacer direction="col" size={2} />
    </Dialog>
  );
};

export const AlertWithContent: React.FC<CustomAlertProps> = ({
  title,
  open,
  onConfirm,
  onClose,
  Content,
  width,
  height,
}) => {
  return (
    <Dialog
      open={open}
      aria-disabled={true}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "80%",
          maxWidth: "800px",
          height: "auto",
          borderRadius: "12px",
        },
      }}
    >
      {title && (
        <DialogTitle
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#1f2937",
            textAlign: "center",
          }}
          id="alert-dialog-title"
        >
          {title}
        </DialogTitle>
      )}
      <DialogContent
        sx={{
          justifyContent: "center",
          padding: "20px",
          // width: width ? `${width}px` : `380px`,
        }}
      >
        {Content && Content}
      </DialogContent>
      {onConfirm && (
        <DialogActions>
          <RowDiv
            style={{
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {onClose && onClose}
            {onConfirm}
          </RowDiv>
        </DialogActions>
      )}
      <Spacer direction="col" size={2} />
    </Dialog>
  );
};
